import { useState, useEffect } from 'react';
import { ShieldCheck, FileText, AlertTriangle, CheckCircle, Download } from 'lucide-react';
import api from '../api/config';
import { generateConfidentialityPDF, CURRENT_VERSION, DOC_POINTS } from '../utils/generateConfidentialityPDF';

// TEST MODE: solo estos emails ven el modal. Poner null para activar para todos.
const TEST_EMAILS = null;

const ConfidentialityModal = ({ user, isSuperAdmin }) => {
  const [show, setShow] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [acceptanceData, setAcceptanceData] = useState(null);
  const [checked, setChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showFullDoc, setShowFullDoc] = useState(false);
  const [error, setError] = useState('');

  const userName = user ? [user.firstName, user.lastName].filter(Boolean).join(' ') : '';
  const userEmail = user?.email || '';

  useEffect(() => {
    if (isSuperAdmin) return;
    if (TEST_EMAILS && !TEST_EMAILS.includes(userEmail)) return;
    checkStatus();
  }, [user, isSuperAdmin]);

  const checkStatus = async () => {
    try {
      const res = await api.get('/confidentiality/status');
      if (!res.data.accepted) {
        setShow(true);
      }
    } catch (err) {
      console.error('Error checking confidentiality:', err);
    }
  };

  const handleAccept = async () => {
    if (!checked) return;
    setLoading(true);
    setError('');
    try {
      // 1. Accept first to get real IP and date from backend
      const res = await api.post('/confidentiality/accept');
      const acceptance = res.data.acceptance;
      setAccepted(true);
      setAcceptanceData(acceptance);

      // 2. Generate PDF with real data (IP, date) and send for email attachment
      try {
        const pdfBase64 = await generateConfidentialityPDF({
          userName,
          userEmail,
          acceptedAt: acceptance?.acceptedAt,
          ipAddress: acceptance?.ipAddress,
          version: CURRENT_VERSION,
          returnBase64: true
        });
        if (pdfBase64) {
          await api.post('/confidentiality/send-pdf', { pdfBase64 });
        }
      } catch (pdfErr) {
        console.error('Error generating/sending PDF for email:', pdfErr);
      }
    } catch (err) {
      setError('Error al registrar. Intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleDecline = () => {
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = '/login';
  };

  const handleDownloadPDF = () => {
    generateConfidentialityPDF({
      userName,
      userEmail,
      acceptedAt: acceptanceData?.acceptedAt,
      ipAddress: acceptanceData?.ipAddress,
      version: CURRENT_VERSION
    });
  };

  if (!show) return null;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 99999,
      background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: "'Space Grotesk', sans-serif"
    }}>
      <div style={{
        background: '#fff', borderRadius: '20px', width: '580px', maxWidth: '94vw',
        maxHeight: '92vh', overflow: 'auto',
        boxShadow: '0 30px 80px rgba(0,0,0,0.35)',
        animation: 'confidentialitySlideIn 0.4s cubic-bezier(0.22, 1, 0.36, 1)'
      }}>
        {/* Header */}
        <div style={{
          background: 'var(--theme-secondary, #000)',
          color: 'var(--theme-primary, #c4fffc)',
          padding: '28px 32px 24px',
          borderRadius: '20px 20px 0 0',
          textAlign: 'center'
        }}>
          <ShieldCheck size={48} style={{ marginBottom: '12px', opacity: 0.9 }} />
          <h2 style={{ margin: '0 0 6px', fontSize: '22px', fontWeight: 700, letterSpacing: '0.5px' }}>
            Compromiso de Confidencialidad
          </h2>
          <p style={{ margin: 0, fontSize: '13px', opacity: 0.7 }}>
            Antes de continuar, debes aceptar este compromiso
          </p>
        </div>

        <div style={{ padding: '28px 32px' }}>
          {accepted ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <CheckCircle size={56} style={{ color: '#16a34a', marginBottom: '16px' }} />
              <h3 style={{ margin: '0 0 8px', fontSize: '18px', color: '#1f2937' }}>
                Compromiso Aceptado
              </h3>
              <p style={{ margin: '0 0 8px', color: '#6b7280', fontSize: '14px' }}>
                Tu aceptación ha sido registrada exitosamente.
              </p>
              <p style={{ margin: '0 0 20px', color: '#6b7280', fontSize: '13px' }}>
                Se ha enviado un correo de confirmación a <strong>{userEmail}</strong>
              </p>

              <button
                onClick={handleDownloadPDF}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '8px',
                  padding: '12px 24px',
                  background: 'var(--theme-secondary, #000)',
                  color: 'var(--theme-primary, #c4fffc)',
                  border: 'none', borderRadius: '12px',
                  fontSize: '14px', fontWeight: 600, cursor: 'pointer',
                  fontFamily: "'Space Grotesk', sans-serif",
                  marginBottom: '16px'
                }}
              >
                <Download size={18} />
                Descargar Comprobante PDF
              </button>

              <br />
              <button
                onClick={() => setShow(false)}
                style={{
                  padding: '12px 40px',
                  background: '#16a34a', color: '#fff',
                  border: 'none', borderRadius: '12px',
                  fontSize: '14px', fontWeight: 700, cursor: 'pointer',
                  fontFamily: "'Space Grotesk', sans-serif"
                }}
              >
                Continuar al Sistema
              </button>
            </div>
          ) : (
            <>
              {(userName || userEmail) && (
                <div style={{
                  background: '#f0f9ff', border: '1px solid #bae6fd',
                  borderRadius: '12px', padding: '14px 18px', marginBottom: '16px',
                  fontSize: '13.5px', color: '#0369a1'
                }}>
                  <strong>Firmante:</strong> {userName || 'N/A'} {userEmail ? `(${userEmail})` : ''}
                </div>
              )}

              <div style={{
                background: '#f8fafc', border: '1px solid #e2e8f0',
                borderRadius: '14px', padding: '20px', marginBottom: '20px'
              }}>
                <p style={{ margin: '0 0 12px', fontSize: '14px', color: '#374151', lineHeight: 1.6 }}>
                  Al acceder a este sistema, te comprometes a:
                </p>
                <ul style={{
                  margin: 0, paddingLeft: '20px', fontSize: '13.5px',
                  color: '#4b5563', lineHeight: 1.8, listStyleType: 'none'
                }}>
                  {DOC_POINTS.map((item, i) => (
                    <li key={i} style={{ marginBottom: '4px' }}>
                      <span style={{ color: 'var(--theme-secondary, #000)', fontWeight: 600, marginRight: '8px' }}>
                        {i + 1}.
                      </span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              <button
                onClick={() => setShowFullDoc(!showFullDoc)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: 'var(--theme-secondary, #000)', fontSize: '13px',
                  fontWeight: 600, fontFamily: "'Space Grotesk', sans-serif",
                  padding: '0 0 16px', textDecoration: 'underline'
                }}
              >
                <FileText size={16} />
                {showFullDoc ? 'Ocultar documento completo' : 'Ver documento completo'}
              </button>

              {showFullDoc && (
                <div style={{
                  background: '#fafafa', border: '1px solid #e5e7eb',
                  borderRadius: '12px', padding: '24px', marginBottom: '20px',
                  maxHeight: '280px', overflowY: 'auto', fontSize: '13px',
                  color: '#374151', lineHeight: 1.7
                }}>
                  <p style={{ fontWeight: 700, textAlign: 'center', marginBottom: '16px', fontSize: '14px' }}>
                    COMPROMISO DE CONFIDENCIALIDAD Y RESGUARDO DE INFORMACIÓN
                  </p>
                  <p style={{ marginBottom: '12px' }}>
                    <strong>Versión:</strong> {CURRENT_VERSION} &nbsp;|&nbsp; <strong>Ley aplicable:</strong> Ley N° 19.628 sobre Protección de la Vida Privada
                  </p>
                  <p style={{ marginBottom: '12px' }}>
                    Yo, {userName || '[nombre del usuario]'}, en mi calidad de usuario autorizado del sistema Paper Boat, declaro haber sido informado/a sobre
                    la naturaleza confidencial de la información a la que tendré acceso, y me comprometo voluntariamente a:
                  </p>
                  {DOC_POINTS.map((point, i) => (
                    <p key={i} style={{ marginBottom: '8px' }}>
                      <strong>{i + 1}.</strong> {point}
                    </p>
                  ))}
                  <p style={{ marginTop: '12px', fontSize: '12px', color: '#6b7280' }}>
                    <strong>Desarrollado y mantenido por:</strong> BCM INTERMEDIACIONES DIGITALES SpA — Software: Paper Boat (www.paper-boat.cl). BCM INTERMEDIACIONES DIGITALES SpA se compromete a resguardar la seguridad e integridad de los datos almacenados conforme a la Ley N° 19.628.
                  </p>
                  <p style={{ fontStyle: 'italic', color: '#6b7280', marginTop: '8px' }}>
                    La aceptación electrónica de este documento tiene plena validez legal conforme a la
                    Ley N° 19.799 sobre Documentos Electrónicos y Firma Electrónica.
                  </p>
                </div>
              )}

              <label style={{
                display: 'flex', alignItems: 'flex-start', gap: '12px',
                cursor: 'pointer', padding: '14px 16px',
                background: checked ? 'rgba(22, 163, 106, 0.06)' : '#f9fafb',
                border: `2px solid ${checked ? '#16a34a' : '#d1d5db'}`,
                borderRadius: '12px', marginBottom: '20px',
                transition: 'all 0.2s'
              }}>
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={(e) => setChecked(e.target.checked)}
                  style={{
                    width: '20px', height: '20px', marginTop: '2px',
                    accentColor: 'var(--theme-secondary, #000)', cursor: 'pointer'
                  }}
                />
                <span style={{ fontSize: '14px', color: '#1f2937', lineHeight: 1.5 }}>
                  He leído y acepto el Compromiso de Confidencialidad y Resguardo de Información.
                  Entiendo que esta aceptación queda registrada con fecha, hora e IP.
                </span>
              </label>

              {error && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca',
                  borderRadius: '8px', marginBottom: '16px', color: '#dc2626', fontSize: '13px'
                }}>
                  <AlertTriangle size={16} />
                  {error}
                </div>
              )}

              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={handleDecline}
                  style={{
                    flex: 1, padding: '13px',
                    background: '#fff', color: '#6b7280',
                    border: '2px solid #d1d5db', borderRadius: '12px',
                    fontSize: '14px', fontWeight: 600, cursor: 'pointer',
                    fontFamily: "'Space Grotesk', sans-serif"
                  }}
                >
                  No Acepto
                </button>
                <button
                  onClick={handleAccept}
                  disabled={!checked || loading}
                  style={{
                    flex: 2, padding: '13px',
                    background: (!checked || loading) ? '#d1d5db' : 'var(--theme-secondary, #000)',
                    color: (!checked || loading) ? '#9ca3af' : 'var(--theme-primary, #c4fffc)',
                    border: '2px solid var(--theme-secondary, #000)',
                    borderRadius: '12px',
                    fontSize: '14px', fontWeight: 700, cursor: checked ? 'pointer' : 'not-allowed',
                    fontFamily: "'Space Grotesk', sans-serif",
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
                  }}
                >
                  <ShieldCheck size={18} />
                  {loading ? 'Registrando...' : 'Acepto el Compromiso'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <style>{`
        @keyframes confidentialitySlideIn {
          from { opacity: 0; transform: translateY(30px) scale(0.96); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
};

export default ConfidentialityModal;
